import * as XLSX from "xlsx";
import {
  formatDate,
  formatGoodsAndServices,
  formatOwners,
  officeLabel,
  registrationNumberOf,
} from "./format";
import { EMPTY_FILTERS } from "./tableFilters";

/**
 * Excel export.
 *
 * Writes a real .xlsx workbook rather than a CSV, because the columns here
 * carry types that CSV loses: application numbers with leading zeros get
 * mangled into integers, dates get re-parsed into whatever locale the
 * opening machine happens to use, and multi-class values with commas break
 * the delimiter. A workbook also lets us ship the search parameters
 * alongside the data, so an exported file explains where it came from.
 *
 * Sheet 1 "Results"  — one row per register record, with an autofilter.
 *                      Carries goods & services, owner address and representative
 *                      when the full record has been loaded for that mark.
 * Sheet 2 "Search"   — the query, source, timing and filters in force.
 */

// Excel refuses a cell longer than 32,767 characters and silently truncates
// or corrupts the file. Goods & services for a broad mark can get long.
const CELL_LIMIT = 32000;
const cell = (text) => String(text || "").slice(0, CELL_LIMIT);

const firstOwner = (r) => r.detail?.owners?.[0];

const COLUMNS = [
  { header: "Trade mark name", width: 30, value: (r) => r.wordMark || "" },
  {
    header: "Application number",
    width: 20,
    value: (r) => r.applicationNumber || "",
  },
  {
    header: "Registration number",
    width: 20,
    value: (r) => registrationNumberOf(r),
  },
  { header: "Owner / applicant", width: 34, value: (r) => formatOwners(r) },
  {
    header: "Owner address",
    width: 40,
    value: (r) => firstOwner(r)?.address || r.details?.applicantAddress || "",
  },
  {
    header: "Representative",
    width: 30,
    value: (r) =>
      (r.detail?.representatives || [])
        .map((rep) => rep.name)
        .filter(Boolean)
        .join(", ") ||
      r.details?.representative ||
      "",
  },
  {
    header: "Jurisdiction",
    width: 24,
    value: (r) => officeLabel(r.jurisdiction),
  },
  { header: "Status", width: 16, value: (r) => r.status || "" },
  { header: "Mark type", width: 16, value: (r) => r.markFeature || "" },
  {
    header: "Nice classes",
    width: 18,
    value: (r) => (r.niceClasses || []).join(", "),
  },
  {
    header: "Goods & services",
    width: 70,
    value: (r) => cell(formatGoodsAndServices(r.goodsAndServices)),
  },
  {
    header: "Filing date",
    width: 14,
    value: (r) => formatDate(r.filingDate),
    type: "date",
  },
  {
    header: "Registration date",
    width: 18,
    value: (r) => formatDate(r.registrationDate),
    type: "date",
  },
  {
    header: "Publication date",
    width: 16,
    value: (r) =>
      formatDate(r.detail?.publicationDate || r.details?.publicationDate),
    type: "date",
  },
  {
    header: "Expiry date",
    width: 14,
    value: (r) => formatDate(r.expiryDate),
    type: "date",
  },
  {
    header: "Source register",
    width: 16,
    value: (r) => (r.source || "").toUpperCase(),
  },
];

const SIMILARITY_COLUMNS = [
  {
    header: "AI similarity (%)",
    width: 17,
    value: (r, ctx) => ctx.similarityMap?.get(r.trademarkId) ?? "",
    type: "number",
  },
  {
    header: "AI reasoning",
    width: 60,
    value: (r, ctx) => ctx.reasoningMap?.get(r.trademarkId) ?? "",
  },
];

const FILTER_LABELS = {
  wordMark: "Trade mark name contains",
  applicationNumber: "Application number contains",
  applicant: "Applicant contains",
  jurisdiction: "Jurisdiction",
  status: "Status",
  niceClass: "Nice class",
  filedFrom: "Filed on or after",
  filedTo: "Filed on or before",
  minSimilarity: "Minimum AI similarity",
};

function timestampSlug() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(
    now.getHours(),
  )}${pad(now.getMinutes())}`;
}

function safeSlug(text) {
  return (
    String(text || "search")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "search"
  );
}

function buildResultsSheet(rows, context) {
  const columns = context.includeSimilarity
    ? [...COLUMNS, ...SIMILARITY_COLUMNS]
    : COLUMNS;

  const header = columns.map((c) => c.header);
  const body = rows.map((row) =>
    columns.map((column) => {
      const value = column.value(row, context);
      // Keep application numbers as text so Excel preserves leading zeros
      // and never reformats them into scientific notation.
      return column.type === "number" && value !== "" ? Number(value) : value;
    }),
  );

  const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
  sheet["!cols"] = columns.map((c) => ({ wch: c.width }));
  sheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { c: 0, r: 0 },
      e: { c: columns.length - 1, r: rows.length },
    }),
  };
  return sheet;
}

function buildSearchSheet({
  query,
  source,
  resultsCount,
  exportedCount,
  filters,
  scope,
}) {
  const rows = [
    ["Export summary", ""],
    ["Generated", new Date().toLocaleString()],
    ["Rows in this file", exportedCount],
    ["Rows returned by the search", resultsCount],
    ["Export scope", scope],
    ["", ""],
    ["Search that produced these results", ""],
    ["Trade mark name", query?.wordMark || ""],
    ["Applicant", query?.applicantName || ""],
    [
      "Jurisdiction",
      query?.jurisdiction ? officeLabel(query.jurisdiction) : "All",
    ],
    ["Nice classes", (query?.niceClasses || []).join(", ") || "All"],
    ["Goods and services note", query?.description || ""],
    ["Register source", (source || "").toUpperCase()],
  ];

  const active = Object.entries(filters || EMPTY_FILTERS).filter(
    ([, v]) => v !== "",
  );
  if (active.length) {
    rows.push(["", ""], ["Table filters applied before export", ""]);
    active.forEach(([key, value]) =>
      rows.push([FILTER_LABELS[key] || key, String(value)]),
    );
  }

  rows.push(
    ["", ""],
    [
      "Note",
      "Register data is supplied by EUIPO/TMview. Similarity scores are automated estimates, not legal advice.",
    ],
  );

  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet["!cols"] = [{ wch: 34 }, { wch: 70 }];
  return sheet;
}

/**
 * Build and download an .xlsx workbook.
 *
 * @param {Object} options
 * @param {Array}  options.rows            records to write
 * @param {Map}    [options.similarityMap] trademarkId -> score
 * @param {Map}    [options.reasoningMap]  trademarkId -> reasoning text
 * @param {Object} [options.query]         the search that produced the rows
 * @param {string} [options.source]        "euipo" | "tmview"
 * @param {number} [options.resultsCount]  total rows before table filtering
 * @param {Object} [options.filters]       table filters in force
 * @param {string} [options.scope]         human description of what was exported
 */
export function exportResultsToExcel({
  rows,
  similarityMap,
  reasoningMap,
  query,
  source,
  resultsCount,
  filters,
  scope = "All rows",
}) {
  if (!rows?.length)
    return { ok: false, reason: "There are no rows to export." };

  const includeSimilarity = Boolean(similarityMap?.size);
  const context = { similarityMap, reasoningMap, includeSimilarity };

  const workbook = XLSX.utils.book_new();
  workbook.Props = {
    Title: `Trade mark search — ${query?.wordMark || ""}`,
    Application: "GetMyFirstTrademark",
    CreatedDate: new Date(),
  };

  XLSX.utils.book_append_sheet(
    workbook,
    buildResultsSheet(rows, context),
    "Results",
  );
  XLSX.utils.book_append_sheet(
    workbook,
    buildSearchSheet({
      query,
      source,
      resultsCount: resultsCount ?? rows.length,
      exportedCount: rows.length,
      filters,
      scope,
    }),
    "Search",
  );

  const filename = `trademarks-${safeSlug(query?.wordMark)}-${timestampSlug()}.xlsx`;
  XLSX.writeFile(workbook, filename, { compression: true });

  return { ok: true, filename, count: rows.length };
}
